/**
 * AppuntamentoFissatoFields — campi per lo stage `appuntamento_fissato`.
 * ★ Fase 59.P2 — estratto da StageTransitionModal.jsx (split componenti oversize).
 * Comportamento e data-testid INVARIATI.
 */
import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { UserCheck } from 'lucide-react';
import { PROFILE_TYPES, HCF_ARCHETYPES, BUDGET_BUCKETS, TIMEFRAMES } from './constants';
import i18n from '../../i18n';

export function AppuntamentoFissatoFields({ form, set, seniorsList }) {
  return (
    <div className="space-y-4">
      <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-900">
        {i18n.t('stageTransition.apptIntro', 'Sta fissando un appuntamento → completa il profilo cliente e assegna il Senior.')}
      </div>

      {/* ★ 2026-07-09: Selettore Senior — OBBLIGATORIO */}
      <div className="rounded-lg border-2 border-indigo-300 bg-indigo-50/40 p-3 space-y-2">
        <Label className="text-xs uppercase tracking-wide font-semibold text-indigo-900 flex items-center gap-1.5">
          <UserCheck className="w-3.5 h-3.5" />
          {i18n.t('stageTransition.seniorManaging', 'Senior che gestirà l\'appuntamento')} *
        </Label>
        <Select
          value={form.assigned_senior_id || ''}
          onValueChange={(v) => set('assigned_senior_id', v)}
        >
          <SelectTrigger data-testid="senior-select" className="bg-white">
            <SelectValue placeholder={seniorsList.length === 0 ? i18n.t('stageTransition.loadingSeniors', 'Caricamento senior…') : i18n.t('stageTransition.selectSenior', '— Seleziona un Senior —')} />
          </SelectTrigger>
          <SelectContent>
            {seniorsList.length === 0 && (
              <SelectItem value="loading" disabled>{i18n.t('stageTransition.loadingSeniors', 'Caricamento senior…')}</SelectItem>
            )}
            {seniorsList.map((s) => (
              <SelectItem key={s.id} value={s.id} data-testid={`senior-option-${s.id}`}>
                {s.name}
                {s.role_label ? ` · ${s.role_label}` : ''}
                {s.senior_countries?.length ? ` · ${s.senior_countries.join('/')}` : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-[10px] text-indigo-800/80">
          {i18n.t('stageTransition.seniorNote', 'Il Senior riceverà la notifica e diventerà il proprietario del lead dallo stage Show in poi. L\'Associate rimane owner per i KPI Lead → Show (target 45%).')}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3">
        <div>
          <Label className="text-xs uppercase tracking-wide">{i18n.t('stageTransition.clientProfile', 'Profilo cliente')} *</Label>
          <div className="grid grid-cols-3 gap-2 mt-1.5">
            {PROFILE_TYPES.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => set('profile_type', p.key)}
                data-testid={`profile-type-${p.key}`}
                className={`text-left rounded-md border p-2.5 transition-colors ${
                  form.profile_type === p.key
                    ? 'border-emerald-600 bg-emerald-50'
                    : 'border-border hover:border-emerald-400'
                }`}
              >
                <div className="text-lg">{p.icon}</div>
                <div className="text-xs font-medium mt-0.5">{p.label}</div>
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wide">{i18n.t('stageTransition.hcfArchetype', 'Archetipo HCF')} *</Label>
          <div className="grid grid-cols-2 gap-2 mt-1.5">
            {HCF_ARCHETYPES.map((a) => (
              <button
                key={a.key}
                type="button"
                onClick={() => set('hcf_archetype', a.key)}
                data-testid={`hcf-archetype-${a.key}`}
                className={`text-left rounded-md border p-2.5 transition-colors ${
                  form.hcf_archetype === a.key
                    ? 'border-amber-600 bg-amber-50'
                    : 'border-border hover:border-amber-400'
                }`}
              >
                <div className="text-sm font-medium">{a.icon} {a.label}</div>
                <div className="text-[10px] text-muted-foreground mt-0.5">{a.description}</div>
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wide" htmlFor="appointment_at">{i18n.t('stageTransition.apptDate', 'Data appuntamento')} *</Label>
          <Input
            id="appointment_at"
            type="datetime-local"
            value={form.appointment_at}
            onChange={(e) => set('appointment_at', e.target.value)}
            data-testid="appointment-at-input"
            className="mt-1"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wide">Budget</Label>
            <Select value={form.budget_range} onValueChange={(v) => set('budget_range', v)}>
              <SelectTrigger data-testid="budget-range-select" className="mt-1">
                <SelectValue placeholder={i18n.t('stageTransition.selectPh', 'Seleziona…')} />
              </SelectTrigger>
              <SelectContent>
                {BUDGET_BUCKETS.map((b) => (
                  <SelectItem key={b.key} value={b.key}>{b.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide">{i18n.t('stageTransition.timeframe', 'Tempistica')}</Label>
            <Select value={form.timeframe} onValueChange={(v) => set('timeframe', v)}>
              <SelectTrigger data-testid="timeframe-select" className="mt-1">
                <SelectValue placeholder={i18n.t('stageTransition.selectPh', 'Seleziona…')} />
              </SelectTrigger>
              <SelectContent>
                {TIMEFRAMES.map((t) => (
                  <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wide">{i18n.t('stageTransition.profileNotes', 'Note sul profilo')}</Label>
          <Textarea
            rows={2}
            value={form.notes_profile}
            onChange={(e) => set('notes_profile', e.target.value)}
            placeholder={i18n.t('stageTransition.profileNotesPh', 'Note opzionali sul profilo del cliente…')}
            data-testid="profile-notes-textarea"
            className="mt-1"
          />
        </div>
      </div>
    </div>
  );
}
